import type { ComponentType } from 'react';

export type SummaryCardTone = 'brand' | 'emerald' | 'amber' | 'gray' | 'teal';

const TONE_STYLES: Record<SummaryCardTone, { card: string; icon: string }> = {
  brand: { card: 'border-[#4369a0]/15 bg-[#4369a0]/5', icon: 'bg-[#4369a0]/10 text-[#4369a0]' },
  emerald: { card: 'border-emerald-100 bg-emerald-50/60', icon: 'bg-emerald-100 text-emerald-700' },
  amber: { card: 'border-amber-100 bg-amber-50/60', icon: 'bg-amber-100 text-amber-700' },
  gray: { card: 'border-gray-200 bg-white', icon: 'bg-gray-100 text-gray-600' },
  teal: { card: 'border-teal-100 bg-teal-50/60', icon: 'bg-teal-100 text-teal-700' },
};

interface SummaryCardProps {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
  tone: SummaryCardTone;
  /** One gray line under the value. */
  hint?: string;
  /** Or a short breakdown under the value, e.g. [['Policy', '11 / 16'], ['Loan', '3 / 4']]. */
  rows?: [label: string, value: string][];
}

/** Top-of-page number card used on Payments, Loans and the Dashboard. */
export function SummaryCard({ icon: Icon, label, value, tone, hint, rows }: SummaryCardProps) {
  const styles = TONE_STYLES[tone];

  return (
    <div className={`rounded-xl border p-4 shadow-sm ${styles.card}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[13px] text-gray-500" title={label}>
            {label}
          </p>
          <p className="mt-1 truncate text-xl font-semibold tabular-nums text-gray-900">{value}</p>
          {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
        </div>
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${styles.icon}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>

      {rows && (
        <dl className="mt-3 space-y-1 border-t border-black/5 pt-2.5">
          {rows.map(([rowLabel, rowValue]) => (
            <div key={rowLabel} className="flex justify-between gap-2 text-xs">
              <dt className="truncate text-gray-500" title={rowLabel}>
                {rowLabel}
              </dt>
              <dd className="whitespace-nowrap font-medium tabular-nums text-gray-700">{rowValue}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
